// src/products/ProductView.js
import React, { useState, useEffect, useRef } from 'react';
import ProductCard from './ProductCard';
import LocationSelector from '../common/LocationSelector';

const ProductView = ({
    products,
    currentPage,
    isLoading,
    totalProducts,
    totalPages,
    searchTerm,
    selectedTherapyAreaFilter,
    setCurrentPage,
    setSearchTerm,
    setSelectedTherapyAreaFilter,
    addToCart,
    userLocation,
    setUserLocation,
    userCoordinates,
    setUserCoordinates
}) => {
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
    const [customItem, setCustomItem] = useState({ name: '', quantity: 1 });
    const [addSuccess, setAddSuccess] = useState(false);
    const productContentRef = useRef(null); // Ref for the content area

    // Scroll to top of product list on page change
    useEffect(() => {
        // Only scroll if the ref is attached to an element
        if (productContentRef.current) {
            // A small timeout can help ensure the scroll happens after the render is fully painted
            setTimeout(() => {
                productContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [currentPage]); // Dependency array ensures this runs only when the page number changes

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page on new search
    };

    const handleCategoryCheckboxChange = (categoryName) => {
        // If the clicked category is already selected, unselect it.
        // Otherwise, select the new category. This makes checkboxes behave like exclusive radio buttons for the backend.
        if (selectedTherapyAreaFilter === categoryName) {
            setSelectedTherapyAreaFilter(''); // Clear filter
        } else {
            setSelectedTherapyAreaFilter(categoryName); // Set new filter
        }
        setSearchTerm(''); // Clear general search when a category is selected/unselected
        setCurrentPage(1); // Reset to first page on new filter
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedTherapyAreaFilter('');
        setCurrentPage(1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handleAddCustomItem = () => {
        if (!customItem.name.trim()) return;
        addToCart({
            _id: `custom-${Date.now()}`,
            name: customItem.name,
            price: 0,
            isCustom: true
        }, customItem.quantity);
        setCustomItem({ name: '', quantity: 1 });
        setAddSuccess(true);
        setTimeout(() => setAddSuccess(false), 2000);
    };

    return (
        <section id="products" className="page bg-white p-8 rounded-lg shadow-xl">
            {/* Title styled to match the sky theme */}
            <h2 className="text-4xl font-bold text-center text-sky-700 mb-8">Our Products</h2>
            <p className="text-center text-gray-600 text-lg mb-6">
                Explore our extensive range of medicines and healthcare products. Use the search bar and filters to find specific items or browse our collection.
            </p>

            {/* Top Search and Reset Bar */}
            <div className="flex flex-col gap-4 mb-8 bg-gray-50 p-4 rounded-lg shadow-sm">
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <div className="w-full sm:flex-1 relative flex items-center bg-white border border-gray-300 rounded-lg">
                        <LocationSelector userLocation={userLocation} setUserLocation={setUserLocation} userCoordinates={userCoordinates} setUserCoordinates={setUserCoordinates} className="rounded-l-lg" />
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                id="product-search"
                                placeholder="Search by name, company..."
                                className="w-full px-4 py-3 bg-transparent focus:outline-none focus:ring-2 focus:ring-sky-500 text-lg pl-10 rounded-r-lg"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                            <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                        </div>
                    </div>
                    <button
                        onClick={handleClearFilters}
                        className="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-300 shadow-md whitespace-nowrap"
                    >
                        Reset
                    </button>
                    <button
                        onClick={() => setIsCustomModalOpen(true)}
                        className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300 shadow-md whitespace-nowrap flex items-center justify-center gap-2"
                    >
                        <i className="fas fa-plus-circle"></i> Add Custom Medicine / Products
                    </button>
                </div>
            </div>

            {/* Main Content: Product Display */}
            <div className="w-full">
                {/* Right Content Area for Products */}
                <div ref={productContentRef} className="w-full relative min-h-[400px]"> {/* Attach ref and add min-height */}
                    {isLoading && (
                        <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10 rounded-lg">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-sky-500"></div>
                        </div>
                    )}
                    <div id="product-list" className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 ${isLoading ? 'opacity-50' : ''}`}>
                        {!isLoading && products.length === 0 ? (
                            <div className="text-center py-12 col-span-full">
                                <p className="text-slate-600 text-lg mb-4">No available products found matching your criteria.</p>
                                <button
                                    onClick={() => setIsCustomModalOpen(true)}
                                    className="bg-emerald-50 text-emerald-700 border-2 border-emerald-500 hover:bg-emerald-500 hover:text-white font-bold py-3 px-8 rounded-xl transition-all duration-300"
                                >
                                    <i className="fas fa-plus-circle mr-2"></i> Type Custom Medicine / Products Instead
                                </button>
                            </div>
                        ) : (
                            products.map(product => (
                                <ProductCard key={product._id} product={product} addToCart={addToCart} />
                            ))
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center space-x-4 mt-8">
                            <button
                                id="prev-page-btn"
                                className="bg-sky-500 hover:bg-sky-600 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span id="page-info" className="text-lg font-semibold text-gray-700">
                                Page {currentPage} of {totalPages} (Total: {totalProducts})
                            </span>
                            <button
                                id="next-page-btn"
                                className="bg-sky-500 hover:bg-sky-600 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom Medicine Modal */}
            {isCustomModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100 p-6 animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                                <i className="fas fa-prescription-bottle-medical text-emerald-500"></i> Add Custom Medicine / Products
                            </h3>
                            <button onClick={() => setIsCustomModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <p className="text-sm text-slate-500 mb-6">Type the name of any OTC or ethical medicine not found in our database. We will confirm the price before billing.</p>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Medicine Name</label>
                                <input 
                                    type="text" 
                                    value={customItem.name} 
                                    onChange={(e) => setCustomItem({ ...customItem, name: e.target.value })} 
                                    placeholder="e.g. Dolo 650, Digene..." 
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all" 
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Quantity</label>
                                <div className="flex items-center gap-4">
                                    <button 
                                        onClick={() => setCustomItem({ ...customItem, quantity: Math.max(1, customItem.quantity - 1) })}
                                        className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"
                                    ><i className="fas fa-minus"></i></button>
                                    <span className="font-bold text-xl">{customItem.quantity}</span>
                                    <button 
                                        onClick={() => setCustomItem({ ...customItem, quantity: customItem.quantity + 1 })}
                                        className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"
                                    ><i className="fas fa-plus"></i></button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex gap-3 flex-col">
                            {addSuccess && (
                                <div className="p-2 bg-emerald-100 text-emerald-700 rounded-lg text-center font-bold text-sm animate-pulse">
                                    Item added to cart! Add another?
                                </div>
                            )}
                            <div className="flex gap-3">
                                <button onClick={() => setIsCustomModalOpen(false)} className="flex-1 py-3 rounded-xl border-2 border-slate-200 font-bold text-slate-600 hover:bg-slate-50 transition-colors">Done / Cancel</button>
                                <button 
                                    onClick={handleAddCustomItem} 
                                    disabled={!customItem.name.trim()}
                                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white rounded-xl font-extrabold transition-all shadow-lg shadow-emerald-500/20"
                                >
                                    Add to Cart
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ProductView;
